import { t } from "@lingui/macro";
import { Button, RichInput } from "@reactive-resume/ui";
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import type { Editor } from "@tiptap/react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

import { SectionHeader } from "@/client/pages/builder/_components/SectionHeader";
import { cn } from "@reactive-resume/utils";
import { CircleNotch, FilePdf, Sparkle, Warning, MagicWand } from "@phosphor-icons/react";
import { useCoverLetter } from "@/client/services/cover-letter/cover-letter";
import { useGenerateCoverLetter } from "@/client/services/cover-letter/generate";
import { useDebouncedUpdateCoverLetter } from "@/client/services/cover-letter/update";
import { useResumeStore } from "@/client/stores/resume";

interface CoverLetterError {
  message: string;
  type: "generation" | "export" | "validation";
}

export const CoverLetterSection = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [coverLetterContent, setCoverLetterContent] = useState("");
  const [localError, setLocalError] = useState<CoverLetterError | null>(null);
  const editorRef = useRef<Editor | null>(null);

  const resume = useResumeStore((state) => state.resume);
  const { generateCoverLetter, loading: isGenerating, error: apiError } = useGenerateCoverLetter();

  // Fetch existing cover letter
  const { data: coverLetter, isLoading: isCoverLetterLoading } = useCoverLetter(resume?.id ?? "");

  // Debounced update hook
  const { debouncedUpdate, cleanup } = useDebouncedUpdateCoverLetter(resume?.id ?? "");

  // Memoized computed values
  const hasJobDescription = useMemo(
    () => Boolean(resume?.job?.description?.trim()),
    [resume?.job?.description],
  );

  const isEmpty = useMemo(() => {
    if (!coverLetterContent) return true;

    // Remove HTML tags and check if there's any meaningful content
    const textOnly = coverLetterContent
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/&nbsp;/g, " ") // Replace non-breaking spaces
      .trim();

    return textOnly === "";
  }, [coverLetterContent]);

  const canGenerate = useMemo(
    () => hasJobDescription && !isGenerating,
    [hasJobDescription, isGenerating],
  );

  // Clear local errors when starting new operations
  const clearError = useCallback(() => {
    setLocalError(null);
  }, []);

  // Load content from database when cover letter is fetched
  useEffect(() => {
    if (coverLetter?.content) {
      setCoverLetterContent(coverLetter.content);
      if (editorRef.current) {
        editorRef.current.commands.setContent(coverLetter.content, false);
      }
    }
  }, [coverLetter]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Enhanced PDF export functionality with proper error handling and formatting
  const handlePdfExport = useCallback(async () => {
    if (isEmpty) {
      setLocalError({
        message: t`Cannot export empty cover letter. Please generate or write content first.`,
        type: "validation",
      });
      return;
    }

    clearError();
    setExportLoading(true);

    try {
      // Get HTML content from editor
      const htmlContent = editorRef.current?.getHTML() || coverLetterContent;

      // Create a new PDF document
      const pdfDoc = await PDFDocument.create();
      const regularFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const boldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
      const italicFont = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
      const boldItalicFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBoldItalic);

      let page = pdfDoc.addPage();
      const { width, height } = page.getSize();

      // Calculate text parameters
      const fontSize = 12;
      const margin = 50;
      const maxWidth = width - 2 * margin;
      const lineHeight = fontSize * 1.6;
      const paragraphSpacing = fontSize * 0.8;
      const listIndent = 30;

      let currentY = height - margin;
      let currentListNumber = 1;

      // Parse HTML and extract formatted content
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");

      // Function to add a new page
      const addNewPage = () => {
        page = pdfDoc.addPage();
        currentY = height - margin;
      };

      // Function to check if we need a new page
      const checkPageSpace = (requiredSpace: number) => {
        if (currentY - requiredSpace < margin) {
          addNewPage();
        }
      };

      // Function to draw text with formatting
      const drawFormattedText = (text: string, font: any, indentLevel = 0, prefix = "") => {
        if (!text.trim()) return;

        const words = text.split(/\s+/).filter((word) => word.length > 0);
        let currentLine = prefix;
        const xPosition = margin + indentLevel * listIndent;
        const availableWidth = maxWidth - indentLevel * listIndent;

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const textWidth = font.widthOfTextAtSize(testLine, fontSize);

          if (textWidth <= availableWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              checkPageSpace(lineHeight);
              page.drawText(currentLine, {
                x: xPosition,
                y: currentY,
                size: fontSize,
                font: font,
                color: rgb(0, 0, 0),
              });
              currentY -= lineHeight;
              currentLine = word;
            } else {
              // Word is too long, break it
              checkPageSpace(lineHeight);
              page.drawText(word, {
                x: xPosition,
                y: currentY,
                size: fontSize,
                font: font,
                color: rgb(0, 0, 0),
              });
              currentY -= lineHeight;
            }
          }
        }

        if (currentLine) {
          checkPageSpace(lineHeight);
          page.drawText(currentLine, {
            x: xPosition,
            y: currentY,
            size: fontSize,
            font: font,
            color: rgb(0, 0, 0),
          });
          currentY -= lineHeight;
        }
      };

      // Function to draw horizontal line
      const drawHorizontalLine = () => {
        checkPageSpace(lineHeight + 10);
        currentY -= 5; // Add some space above
        page.drawLine({
          start: { x: margin, y: currentY },
          end: { x: width - margin, y: currentY },
          thickness: 1,
          color: rgb(0.5, 0.5, 0.5), // Gray color
        });
        currentY -= 10; // Add some space below
      };

      // Function to process element content with proper formatting detection
      const processElementContent = (
        node: Node,
        parentElement: Element,
        indentLevel = 0,
        prefix = "",
      ) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            // Check parent formatting
            const isBold = parentElement.closest("strong, b") !== null;
            const isItalic = parentElement.closest("em, i") !== null;

            let font = regularFont;
            if (isBold && isItalic) {
              font = boldItalicFont;
            } else if (isBold) {
              font = boldFont;
            } else if (isItalic) {
              font = italicFont;
            }

            drawFormattedText(text, font, indentLevel, prefix);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const tagName = element.tagName.toLowerCase();

          if (["strong", "b", "em", "i", "u"].includes(tagName)) {
            // Handle inline formatting
            for (const child of Array.from(element.childNodes)) {
              processElementContent(child, element, indentLevel, prefix);
            }
          } else if (tagName === "br") {
            currentY -= lineHeight;
          } else {
            // Process other elements
            for (const child of Array.from(element.childNodes)) {
              processElementContent(child, element, indentLevel, prefix);
            }
          }
        }
      };

      // Function to process text nodes with formatting
      const processNode = (node: Node, indentLevel = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            drawFormattedText(text, regularFont, indentLevel);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const tagName = element.tagName.toLowerCase();

          switch (tagName) {
            case "p":
              // Process paragraph content
              for (const child of Array.from(element.childNodes)) {
                processElementContent(child, element, indentLevel);
              }
              // Add paragraph spacing
              currentY -= paragraphSpacing;
              break;

            case "br":
              currentY -= lineHeight;
              break;

            case "hr":
              // Draw horizontal line
              drawHorizontalLine();
              break;

            case "ol":
              // Reset list numbering and process ordered list
              currentListNumber = 1;
              for (const child of Array.from(element.childNodes)) {
                if (
                  child.nodeType === Node.ELEMENT_NODE &&
                  (child as Element).tagName.toLowerCase() === "li"
                ) {
                  processListItem(child as Element, indentLevel + 1, currentListNumber);
                  currentListNumber++;
                }
              }
              currentY -= paragraphSpacing; // Add spacing after list
              break;

            case "ul":
              // Process unordered list
              for (const child of Array.from(element.childNodes)) {
                if (
                  child.nodeType === Node.ELEMENT_NODE &&
                  (child as Element).tagName.toLowerCase() === "li"
                ) {
                  processListItem(child as Element, indentLevel + 1, null, "•");
                }
              }
              currentY -= paragraphSpacing; // Add spacing after list
              break;

            case "li":
              // List items should be handled by their parent ol/ul
              break;

            case "a":
              // Handle links - show text with URL in parentheses
              const linkText = element.textContent?.trim();
              const href = element.getAttribute("href");
              if (linkText) {
                let displayText = linkText;
                if (href && href !== linkText) {
                  displayText = `${linkText} (${href})`;
                }
                // Use italic font for links to distinguish them
                drawFormattedText(displayText, italicFont, indentLevel);
              }
              break;

            case "strong":
            case "b":
              const boldText = element.textContent?.trim();
              if (boldText) {
                drawFormattedText(boldText, boldFont, indentLevel);
              }
              break;

            case "em":
            case "i":
              const italicText = element.textContent?.trim();
              if (italicText) {
                drawFormattedText(italicText, italicFont, indentLevel);
              }
              break;

            case "u":
              // For underlined text, we'll use regular font (PDF-lib doesn't have built-in underline)
              const underlinedText = element.textContent?.trim();
              if (underlinedText) {
                drawFormattedText(underlinedText, regularFont, indentLevel);
              }
              break;

            default:
              // Process child nodes for other elements
              for (const child of Array.from(element.childNodes)) {
                processNode(child, indentLevel);
              }
          }
        }
      };

      // Function to process list items
      const processListItem = (
        element: Element,
        indentLevel: number,
        number?: number | null,
        bullet = "",
      ) => {
        let prefix = "";
        if (number !== null && number !== undefined) {
          prefix = `${number}. `;
        } else if (bullet) {
          prefix = `${bullet} `;
        }

        // Process list item content
        for (const child of Array.from(element.childNodes)) {
          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent?.trim();
            if (text) {
              drawFormattedText(text, regularFont, indentLevel, prefix);
              prefix = ""; // Only add prefix to first text node
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childElement = child as Element;
            const childTagName = childElement.tagName.toLowerCase();

            if (["strong", "b"].includes(childTagName)) {
              const text = childElement.textContent?.trim();
              if (text) {
                drawFormattedText(text, boldFont, indentLevel, prefix);
                prefix = ""; // Only add prefix to first text node
              }
            } else if (["em", "i"].includes(childTagName)) {
              const text = childElement.textContent?.trim();
              if (text) {
                drawFormattedText(text, italicFont, indentLevel, prefix);
                prefix = ""; // Only add prefix to first text node
              }
            } else if (childTagName === "a") {
              const linkText = childElement.textContent?.trim();
              const href = childElement.getAttribute("href");
              if (linkText) {
                let displayText = linkText;
                if (href && href !== linkText) {
                  displayText = `${linkText} (${href})`;
                }
                drawFormattedText(displayText, italicFont, indentLevel, prefix);
                prefix = ""; // Only add prefix to first text node
              }
            } else {
              // Handle other nested elements
              processElementContent(child, element, indentLevel, prefix);
              prefix = ""; // Only add prefix to first element
            }
          }
        }
      };

      // Process the document
      const bodyContent = doc.body || doc.documentElement;
      for (const child of Array.from(bodyContent.childNodes)) {
        processNode(child);
      }

      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cover-letter-${resume?.data?.basics?.name || "untitled"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF export failed:", error);
      setLocalError({
        message: t`Failed to export cover letter as PDF. Please try again.`,
        type: "export",
      });
    } finally {
      setExportLoading(false);
    }
  }, [isEmpty, coverLetterContent, resume?.data?.basics?.name, clearError]);

  // Enhanced generation with better error handling
  const handleGenerateCoverLetter = useCallback(async () => {
    if (!hasJobDescription) {
      setLocalError({
        message: t`Job description is required to generate a personalized cover letter.`,
        type: "validation",
      });
      return;
    }

    clearError();

    try {
      const result = await generateCoverLetter({
        resumeId: resume.id,
        jobDescription: resume.job!.description,
      });

      if (result?.coverLetter) {
        setCoverLetterContent(result.coverLetter);
        // Ensure editor content is updated
        if (editorRef.current) {
          editorRef.current.commands.setContent(result.coverLetter, true);
        }
      } else {
        throw new Error("No cover letter content received");
      }
    } catch (error) {
      console.error("Generation failed:", error);
      setLocalError({
        message: t`Failed to generate cover letter. Please check your connection and try again.`,
        type: "generation",
      });
    }
  }, [hasJobDescription, generateCoverLetter, resume?.id, resume?.job, clearError]);

  // Handle content changes from the editor with debounced persistence
  const handleContentChange = useCallback(
    (content: string) => {
      setCoverLetterContent(content);
      clearError();

      // Only debounce update if content is not empty and resume exists
      if (content.trim() && resume?.id) {
        debouncedUpdate(content);
      }
    },
    [clearError, resume?.id, debouncedUpdate],
  );

  // Store editor reference
  const handleEditorInit = useCallback((editor: Editor) => {
    editorRef.current = editor;
    return null;
  }, []);

  // Combined error from API or local state
  const displayError = apiError || localError;

  return (
    <section
      id="cover-letter"
      className="grid gap-y-2"
      role="region"
      aria-labelledby="cover-letter-title"
    >
      <SectionHeader icon="notes" title={t`Cover Letter`} />

      <main className="grid gap-y-1">
        {/* Error Display */}
        {displayError && (
          <div
            className="animate-in fade-in-0 slide-in-from-top-2 mb-2 flex items-center gap-2 rounded-sm bg-red-50 p-2 text-red-700 duration-300 dark:bg-red-900/20 dark:text-red-400"
            role="alert"
            aria-live="polite"
          >
            <Warning size="1em" className="animate-in zoom-in-50 delay-100 duration-200" />
            <span className="animate-in fade-in-0 text-xs delay-150 duration-300">
              {typeof displayError === "string" ? displayError : displayError.message}
            </span>
          </div>
        )}

        {/* Generation Section */}
        {isEmpty && (
          <div className="animate-in fade-in-0 slide-in-from-top-4 mb-4 space-y-3 duration-500 ease-out">
            {hasJobDescription && (
              <div className="animate-in fade-in-0 slide-in-from-left-4 dark:bg-background/60 rounded-md bg-green-50 p-3 delay-200 duration-400 ease-out">
                <div className="flex items-start gap-2">
                  <div>
                    <p className="animate-in fade-in-0 text-sm delay-400 duration-300">
                      {t`Create a personalized cover letter that perfectly matches your resume to your job posting. Our AI analyzes your experience and the job requirements to craft compelling, professional content that highlights your best qualifications.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!hasJobDescription && (
              <div className="animate-in fade-in-0 slide-in-from-left-4 rounded-md bg-amber-50 p-3 delay-200 duration-400 ease-out dark:bg-amber-900/20">
                <div className="flex items-start gap-2">
                  <div>
                    <p className="animate-in fade-in-0 text-xs text-amber-800 delay-400 duration-300 dark:text-amber-200">
                      {t`Add a job description to your resume first to generate a personalized cover letter that matches the specific job requirements.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              size="lg"
              className={cn(
                "bg-primary text-primary-foreground hover:bg-primary/90 animate-in fade-in-0 slide-in-from-bottom-4 w-full justify-center gap-2 py-3 text-sm font-medium transition-all delay-300 duration-300 ease-out hover:scale-[1.02] active:scale-[0.98]",
                (!canGenerate || isGenerating) &&
                  "pointer-events-none cursor-not-allowed opacity-50",
              )}
              onClick={handleGenerateCoverLetter}
              disabled={!canGenerate || isGenerating}
              aria-describedby={!hasJobDescription ? "job-description-hint" : undefined}
            >
              {isGenerating ? (
                <>
                  <CircleNotch className="animate-spin" size="1.2em" aria-hidden="true" />
                  <span>{t`Generating cover letter...`}</span>
                </>
              ) : (
                <>
                  <MagicWand
                    size="1.2em"
                    aria-hidden="true"
                    className="animate-in zoom-in-50 delay-500 duration-200"
                  />
                  <span className="animate-in fade-in-0 delay-500 duration-300">{t`Generate Personalized Cover Letter`}</span>
                </>
              )}
            </Button>
          </div>
        )}

        {/* Rich Text Editor with Instructions */}
        {!isEmpty && (
          <>
            <div className="animate-in fade-in-0 slide-in-from-bottom-4 space-y-2 delay-150 duration-400">
              <RichInput
                content={coverLetterContent}
                onChange={handleContentChange}
                disabled={isGenerating}
                footer={handleEditorInit}
                aria-label={t`Cover letter content`}
                editorClassName={cn(
                  "h-full max-h-none",
                  "[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb:hover]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent",
                  "dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:[&::-webkit-scrollbar-thumb:hover]:bg-gray-500",
                )}
                className="h-[300px]"
                placeholder={
                  hasJobDescription
                    ? t`Your AI-generated cover letter will appear here, or start typing to write your own...`
                    : t`Add a job description to your resume first, then generate a personalized cover letter`
                }
              />
            </div>

            {/* Export Section */}
            <div className="border-border animate-in fade-in-0 slide-in-from-bottom-2 space-y-3 border-t pt-4 delay-100 duration-400">
              <div className="flex items-center justify-between">
                <div className="animate-in fade-in-0 slide-in-from-left-2 delay-200 duration-300">
                  <h4 className="hover:text-primary text-sm font-medium transition-colors duration-200">{t`Download Your Cover Letter`}</h4>
                  <p className="text-muted-foreground text-xs transition-colors duration-200">
                    {t`Export as a PDF file for professional applications and submissions`}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className={cn(
                    "animate-in fade-in-0 slide-in-from-right-2 flex items-center gap-2 px-4 py-2 transition-all delay-300 duration-300 ease-out hover:scale-105 hover:shadow-md active:scale-95",
                    (exportLoading || isEmpty) &&
                      "pointer-events-none cursor-not-allowed opacity-50",
                  )}
                  onClick={handlePdfExport}
                  disabled={exportLoading || isEmpty}
                  title={isEmpty ? t`Write or generate content first` : t`Download as PDF file`}
                >
                  {exportLoading ? (
                    <CircleNotch className="animate-spin" size="1.2em" aria-hidden="true" />
                  ) : (
                    <FilePdf
                      size="1.2em"
                      aria-hidden="true"
                      className="transition-transform duration-200 group-hover:scale-110"
                    />
                  )}
                  <span className="transition-all duration-200">
                    {exportLoading ? t`Preparing...` : t`Download`}
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Help Text and Instructions */}
        {!hasJobDescription && isEmpty && (
          <div className="animate-in fade-in-0 slide-in-from-bottom-6 mt-4 space-y-3 delay-200 duration-600 ease-out">
            <div className="animate-in fade-in-0 scale-in-95 rounded-lg border-2 border-dashed border-gray-300 p-6 text-center delay-400 duration-500 ease-out dark:border-gray-600">
              <div className="animate-in zoom-in-0 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 delay-600 duration-400 ease-out dark:bg-gray-800">
                <MagicWand className="animate-in zoom-in-50 h-6 w-6 text-gray-400 delay-800 duration-300" />
              </div>
              <h3 className="animate-in fade-in-0 slide-in-from-top-2 mb-2 text-sm font-medium text-gray-900 delay-700 duration-300 dark:text-gray-100">
                {t`Get Started with AI Cover Letter Generation`}
              </h3>
              <p
                className="text-muted-foreground animate-in fade-in-0 mb-4 text-sm delay-800 duration-400"
                id="job-description-hint"
              >
                {t`To create a personalized cover letter, first add a job description to your resume. This helps our AI understand what the employer is looking for.`}
              </p>
              <div className="animate-in fade-in-0 slide-in-from-bottom-2 mx-auto max-w-md text-left delay-900 duration-400">
                <p className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t`How to add a job description:`}
                </p>
                <ol className="text-muted-foreground space-y-1 text-sm">
                  <li className="animate-in fade-in-0 slide-in-from-left-2 delay-1000 duration-300">
                    1. {t`Go to your resume's job/position section`}
                  </li>
                  <li className="animate-in fade-in-0 slide-in-from-left-2 delay-1100 duration-300">
                    2. {t`Paste the full job posting or key requirements`}
                  </li>
                  <li className="animate-in fade-in-0 slide-in-from-left-2 delay-1200 duration-300">
                    3. {t`Return here to generate your tailored cover letter`}
                  </li>
                </ol>
              </div>
            </div>

            <div className="animate-in fade-in-0 slide-in-from-right-4 rounded-md bg-blue-50 p-4 delay-600 duration-500 ease-out dark:bg-blue-900/20">
              <h4 className="animate-in fade-in-0 mb-2 text-sm font-medium text-blue-900 delay-800 duration-300 dark:text-blue-100">
                {t`Why add a job description?`}
              </h4>
              <ul className="space-y-1 text-xs text-blue-800 dark:text-blue-200">
                <li className="animate-in fade-in-0 slide-in-from-left-1 delay-900 duration-200">
                  • {t`AI matches your skills to specific job requirements`}
                </li>
                <li className="animate-in fade-in-0 slide-in-from-left-1 delay-1000 duration-200">
                  • {t`Uses the company's language and keywords`}
                </li>
                <li className="animate-in fade-in-0 slide-in-from-left-1 delay-1100 duration-200">
                  • {t`Highlights your most relevant experiences`}
                </li>
                <li className="animate-in fade-in-0 slide-in-from-left-1 delay-1200 duration-200">
                  • {t`Creates a more compelling and targeted letter`}
                </li>
                <li className="animate-in fade-in-0 slide-in-from-left-1 delay-1300 duration-200">
                  • {t`Increases your chances of getting past ATS systems`}
                </li>
              </ul>
            </div>
          </div>
        )}
      </main>
    </section>
  );
};
